import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/safeToast';
import {
  FileText,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  User,
  Shield,
  DollarSign,
  Key,
  LogIn,
  LogOut
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const AuditLogs = ({ LayoutComponent = Layout }) => {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({});
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 50,
    action: 'all',
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', filters.page);
      params.append('per_page', filters.per_page);
      if (filters.action && filters.action !== 'all') params.append('action', filters.action);

      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data.data || []);
      setMeta(response.data.meta || {});
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getActionIcon = (action) => {
    if (action.includes('LOGIN') || action.includes('LOGOUT')) {
      return action.includes('LOGOUT') ? (
        <LogOut className="w-4 h-4 text-[#F08080]" />
      ) : (
        <LogIn className="w-4 h-4 text-[#1C472B]" />
      );
    }
    if (action.includes('USER')) return <User className="w-4 h-4 text-blue-500" />;
    if (action.includes('2FA') || action.includes('PASSWORD'))
      return <Shield className="w-4 h-4 text-amber-500" />;
    if (action.includes('PAYMENT') || action.includes('ACCOUNT'))
      return <DollarSign className="w-4 h-4 text-[#1C472B]" />;
    return <FileText className="w-4 h-4 text-slate-400" />;
  };

  const getActionBadgeClass = (action) => {
    if (action.includes('DELETE') || action.includes('DISABLE'))
      return 'bg-red-50 text-[#F08080]';
    if (action.includes('CREATE') || action.includes('ENABLE'))
      return 'bg-[#E6F7F3] text-[#1C472B]';
    if (action.includes('UPDATE') || action.includes('CHANGE'))
      return 'bg-amber-50 text-amber-700';
    return 'bg-slate-100 text-slate-600';
  };

  const actionTypes = [
    { value: 'all', label: 'All Actions' },
    { value: 'USER_REGISTERED', label: 'User Registered' },
    { value: 'USER_LOGIN', label: 'User Login' },
    { value: 'EMAIL_VERIFIED', label: 'Email Verified' },
    { value: '2FA_ENABLED', label: '2FA Enabled' },
    { value: '2FA_DISABLED', label: '2FA Disabled' },
    { value: 'PASSWORD_CHANGED', label: 'Password Changed' },
    { value: 'PAYMENT_CREATED', label: 'Payment Created' },
    { value: 'ACCOUNT_CREATED', label: 'Account Created' },
    { value: 'ACCOUNT_DELETED', label: 'Account Deleted' },
    { value: 'USER_CREATED_BY_ADMIN', label: 'User Created (Admin)' },
    { value: 'USER_DELETED', label: 'User Deleted' },
    { value: 'USER_ROLE_UPDATED', label: 'Role Updated' },
    { value: 'EXPORT_TRANSACTIONS', label: 'Export Transactions' },
  ];

  const filteredLogs = logs.filter((log) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      log.action?.toLowerCase().includes(term) ||
      log.user_id?.toLowerCase().includes(term) ||
      JSON.stringify(log.details)?.toLowerCase().includes(term)
    );
  });

  return (
    <LayoutComponent>
      <div data-testid="audit-logs-page" className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-extrabold text-[#0F172A] ">
            Audit Logs
          </h1>
          <p className="text-slate-500 mt-1">Track all activities and changes in your account</p>
        </div>

        {/* Filters */}
        <Card className="card">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                <Input
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field !pl-10"
                  data-testid="search-logs"
                />
              </div>

              {/* Action Filter */}
              <Select
                value={filters.action}
                onValueChange={(value) => setFilters({ ...filters, action: value, page: 1 })}
              >
                <SelectTrigger className="w-52" data-testid="action-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by action" />
                </SelectTrigger>
                <SelectContent>
                  {actionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold ">
              Activity Log
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="spinner w-8 h-8" />
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-500">No audit logs found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="audit-logs-table">
                    <thead>
                      <tr>
                        <th className="table-header">Action</th>
                        <th className="table-header">User ID</th>
                        <th className="table-header">Details</th>
                        <th className="table-header">Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map((log) => (
                        <tr key={log.id} className="table-row">
                          <td className="table-cell">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                                {getActionIcon(log.action)}
                              </div>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-semibold ${getActionBadgeClass(
                                  log.action
                                )}`}
                              >
                                {log.action?.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </td>
                          <td className="table-cell">
                            <code className="text-xs font-mono text-slate-500">
                              {log.user_id?.slice(0, 8)}...
                            </code>
                          </td>
                          <td className="table-cell">
                            <div className="max-w-xs">
                              {log.details && Object.keys(log.details).length > 0 ? (
                                <div className="text-sm text-slate-600">
                                  {Object.entries(log.details).map(([key, value]) => (
                                    <span key={key} className="mr-3">
                                      <span className="text-slate-400">{key}:</span>{' '}
                                      <span className="font-medium">{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400 text-sm">—</span>
                              )}
                            </div>
                          </td>
                          <td className="table-cell text-slate-500 text-sm">
                            {formatDate(log.timestamp)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {meta.total > 0 && (
                  <div className="flex items-center justify-between p-4 border-t border-slate-100">
                    <p className="text-sm text-slate-500">
                      Page {filters.page} of {meta.last_page || 1}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                        disabled={filters.page === 1}
                        data-testid="prev-page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                        disabled={filters.page >= (meta.last_page || 1)}
                        data-testid="next-page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </LayoutComponent>
  );
};

export default AuditLogs;
